// components/client_servicing/sections/OverServicing.jsx
import React, { useMemo, useState } from 'react';
import { InformationCircleIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatCurrency, formatPercentage } from '../../../utils';

// KPI Definitions
const KPI_DEFINITIONS = {
  billableIncurred: "The total value of billable time and expenses incurred for the client during the period. Represents the actual cost of service delivery based on your standard rates. When margin is applied, the adjusted amount (cost + margin%) is shown below.",
  invoicedFees: "The total amount invoiced and collected from the client during the period. For retainers, this is typically the fixed monthly fee. For projects, this is the sum of all invoices.",
  overUnderService: "The difference between fees collected and billable incurred (with margin applied if selected). Negative values (red) indicate over-servicing where you delivered more value than was paid for. Positive values (green) indicate under-servicing where the client paid more than the work delivered. Margin helps account for overhead and profit targets.",
  completionPercentage: "The percentage of the retainer or project budget consumed by billable work (including margin if applied). Values over 100% (red) indicate scope creep or budget overruns.",
  retainerCycle: "Monthly retainer clients are grouped by their billing cycle date (1st or 15th of the month). This helps identify performance patterns across different billing periods.",
  projectBilling: "Fixed-price or project-based work tracked separately from retainers. Shows lifetime totals from project start to last activity date.",
  currentCycle: "Shows the current billing period in progress. For 1st cycle: from the 1st of the month to today. For 15th cycle: from the 15th of last month to today (if before the 15th), or from the 15th to today (if on or after the 15th).",
  lastCompletedCycle: "Shows the most recently completed billing period. For 1st cycle: the full period from 1st to end of last month. For 15th cycle: the full period from 15th of two months ago to 14th of last month.",
  margin: "Apply a percentage markup to billable costs to account for overhead, profit targets, and operational expenses. This shows the true profitability after covering all costs. For example, 20% margin means you need to charge 20% above cost to break even on overhead."
};

const OverServicing = ({ data, loading, error, onRefresh }) => {
  const [marginType, setMarginType] = useState('60'); // '0', '25', '50', '75', 'custom'
  const [customMargin, setCustomMargin] = useState('15');
  const [showMarginSelector, setShowMarginSelector] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  
  // Calculate the active margin percentage
  const activeMargin = marginType === 'custom' ? parseFloat(customMargin) || 0 : parseFloat(marginType);
  
  const processedData = useMemo(() => {
    if (!data?.client_summaries) {
      return { 
        billingBy1st: [], 
        billingBy15th: [], 
        projectClients: [],
        groupedProjects: [],
        cycle1stDates: '',
        cycle15thDates: '',
        periodLabel: ''
      };
    }
    
    const allClients = data.client_summaries;
    
    // Filter by billing_cycle field (not billing_cycle property)
    const billingBy1st = allClients.filter(client => 
      client.billing_cycle === '1st'
    );
    
    const billingBy15th = allClients.filter(client => 
      client.billing_cycle === '15th'
    );
    
    const projectClients = allClients.filter(client => 
      client.billing_cycle === 'project' || client.client_type === 'project'
    );

    // Group projects by parent_customer
    const projectGroups = projectClients.reduce((groups, project) => {
      const parent = project.parent_customer || 'Unknown';
      if (!groups[parent]) {
        groups[parent] = [];
      }
      groups[parent].push(project);
      return groups;
    }, {});

    // Create aggregated project groups
    const groupedProjects = Object.entries(projectGroups).map(([parentCustomer, projects]) => {
      // Aggregate metrics
      const totalTimeCost = projects.reduce((sum, p) => sum + (p.billable_incurred || p.time_cost || 0), 0);
      const totalFees = projects.reduce((sum, p) => sum + (p.fees_collected || p.total_fees || 0), 0);
      const totalOverservice = projects.reduce((sum, p) => sum + (p.overservice_amount || 0), 0);
      
      // Date ranges - earliest start to latest activity
      const projectStartDates = projects
        .map(p => p.project_start_date)
        .filter(Boolean)
        .sort();
      const lastActivityDates = projects
        .map(p => p.last_activity_date)
        .filter(Boolean)
        .sort();
      
      const earliestStart = projectStartDates[0] || null;
      const latestActivity = lastActivityDates[lastActivityDates.length - 1] || null;
      
      // Completion percentage
      const completionPct = totalFees > 0 ? (totalTimeCost / totalFees) * 100 : 0;
      
      return {
        parent_customer: parentCustomer,
        projects: projects,
        project_count: projects.length,
        billable_incurred: totalTimeCost,
        fees_collected: totalFees,
        overservice_amount: totalOverservice,
        completion_percentage: completionPct,
        project_start_date: earliestStart,
        last_activity_date: latestActivity,
        client_type: 'project',
        billing_cycle: 'project'
      };
    });

    // Format cycle dates
    const formatCycleDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString + 'T00:00:00'); // Force local time
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric'
      });
    };

    // Get cycle date ranges from FIRST client in each group
    const cycle1stDates = billingBy1st.length > 0 && billingBy1st[0].cycle_start ? 
      `${formatCycleDate(billingBy1st[0].cycle_start)} - ${formatCycleDate(billingBy1st[0].cycle_end)}` 
      : '';
      
    const cycle15thDates = billingBy15th.length > 0 && billingBy15th[0].cycle_start ? 
      `${formatCycleDate(billingBy15th[0].cycle_start)} - ${formatCycleDate(billingBy15th[0].cycle_end)}` 
      : '';

    // Get period label from metadata
    const periodLabel = data.cycle_metadata?.period_label || '';
    
    return {
      billingBy1st,
      billingBy15th,
      projectClients,
      groupedProjects,
      cycle1stDates,
      cycle15thDates,
      periodLabel
    };
  }, [data]);

  const { billingBy1st, billingBy15th, projectClients, groupedProjects, cycle1stDates, cycle15thDates, periodLabel } = processedData;

  // Helper function to format dates for projects
  const formatProjectDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString + 'T00:00:00'); // Force local time
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Toggle project expansion
  const toggleProjectExpansion = (parentCustomer) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(parentCustomer)) {
        newSet.delete(parentCustomer);
      } else {
        newSet.add(parentCustomer);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-500">Loading billing analysis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        <p>Error loading billing data: {error}</p>
        {onRefresh && (
          <button 
            onClick={onRefresh}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (!data?.client_summaries?.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No billing analysis data available for the selected period</p>
        {periodLabel && <p className="text-sm mt-2">{periodLabel}</p>}
      </div>
    );
  }
  
  const getOverserviceColor = (amount) => {
    if (amount > 0) return 'text-green-600';
    if (amount < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const TableRow = ({ client, index, section }) => {
    const clientName = client.customer || `client-${index}`;
    const parentName = client.parent_customer;
    const uniqueKey = `${section}-${index}-${clientName}`;
    
    // Determine if this is a project
    const isProject = client.client_type === 'project' || client.billing_cycle === 'project';
    
    // Calculate billable with margin
    const billableWithMargin = (client.billable_incurred || 0) * (1 + activeMargin / 100);
    
    // Calculate adjusted over/underservice
    const adjustedOverservice = (client.fees_collected || 0) - billableWithMargin;
    
    // Calculate adjusted completion percentage
    const adjustedCompletionPct = client.fees_collected > 0 
      ? (billableWithMargin / client.fees_collected) * 100 
      : client.completion_percentage;
    
    return (
      <tr key={uniqueKey} className="hover:bg-gray-50">
        <td className="px-3 py-3 text-left">
          {/* For retainers: Show parent customer name as primary */}
          {!isProject && parentName && (
            <div className="font-medium text-gray-900">{parentName}</div>
          )}
          
          {/* For projects: Show customer name (sub-customer/project name) as primary */}
          {isProject && (
            <>
              <div className="font-medium text-gray-900">
                {parentName} - {clientName.split(":")[1]?.trim() || ""}
              </div>
              {/* Show project dates as subtitle */}
              <div className="text-xs text-gray-600 mt-1">
                Project Start Date → Last Activity: {formatProjectDate(client.project_start_date)} → {formatProjectDate(client.last_activity_date)}
              </div>
            </>
          )}
          
          {/* Fallback if no parent and not a project */}
          {!isProject && !parentName && (
            <div className="font-medium text-gray-900">{clientName}</div>
          )}
          
          {/* Show additional project info if available */}
          {isProject && client.project_name && client.project_name !== clientName && (
            <div className="text-xs text-gray-500 mt-0.5">Project: {client.project_name}</div>
          )}
        </td>
        <td className="px-3 py-3 text-center text-gray-900">
          {formatCurrency(billableWithMargin)}
        </td>
        <td className="px-3 py-3 text-center text-gray-900">
          {formatCurrency(client.fees_collected)}
        </td>
        <td className={`px-3 py-3 text-center font-medium ${getOverserviceColor(adjustedOverservice)}`}>
          {formatCurrency(adjustedOverservice)}
        </td>
        <td className="px-3 py-3 text-center">
          <span className={`${adjustedCompletionPct > 100 ? 'text-red-600 font-semibold' : 'text-gray-900'}`}>
            {formatPercentage(adjustedCompletionPct / 100)}
          </span>
        </td>
      </tr>
    );
  };

  const ProjectGroupRow = ({ group, index }) => {
    const isExpanded = expandedProjects.has(group.parent_customer);
    const hasMultipleProjects = group.project_count > 1;
    const uniqueKey = `project-group-${index}-${group.parent_customer}`;
    
    // If single project, render as regular TableRow
    if (!hasMultipleProjects) {
      const singleProject = group.projects[0];
      return (
        <TableRow 
          key={uniqueKey} 
          client={{
            ...singleProject,
            billable_incurred: singleProject.billable_incurred || singleProject.time_cost,
            fees_collected: singleProject.fees_collected || singleProject.total_fees,
            client_type: 'project',
            billing_cycle: 'project'
          }} 
          index={index} 
          section="project" 
        />
      );
    }
    
    // Calculate billable with margin for multi-project group
    const billableWithMargin = (group.billable_incurred || 0) * (1 + activeMargin / 100);
    
    // Calculate adjusted over/underservice
    const adjustedOverservice = (group.fees_collected || 0) - billableWithMargin;
    
    // Calculate adjusted completion percentage
    const adjustedCompletionPct = group.fees_collected > 0 
      ? (billableWithMargin / group.fees_collected) * 100 
      : group.completion_percentage;
    
    return (
      <>
        {/* Parent Row - Aggregated (clickable) */}
        <tr 
          key={uniqueKey} 
          onClick={() => toggleProjectExpansion(group.parent_customer)}
          className="hover:bg-gray-50 cursor-pointer"
        >
          <td className="px-3 py-3 text-left">
            <div>
              <div className="font-semibold text-gray-900">
                {group.parent_customer}
                <span className="ml-2 text-xs font-normal text-gray-600">
                  ({group.project_count} projects)
                </span>
              </div>
              <div className="text-xs text-gray-600 mt-1">
                {formatProjectDate(group.project_start_date)} → {formatProjectDate(group.last_activity_date)}
              </div>
            </div>
          </td>
          <td className="px-3 py-3 text-center font-semibold text-gray-900">
            {formatCurrency(billableWithMargin)}
          </td>
          <td className="px-3 py-3 text-center font-semibold text-gray-900">
            {formatCurrency(group.fees_collected)}
          </td>
          <td className={`px-3 py-3 text-center font-semibold ${getOverserviceColor(adjustedOverservice)}`}>
            {formatCurrency(adjustedOverservice)}
          </td>
          <td className="px-3 py-3 text-center">
            <div className="flex items-center justify-center gap-2">
              <span className={`font-semibold ${adjustedCompletionPct > 100 ? 'text-red-600' : 'text-gray-900'}`}>
                {formatPercentage(adjustedCompletionPct / 100)}
              </span>
              {/* Chevron at the end */}
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4 text-gray-600" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-gray-600" />
              )}
            </div>
          </td>
        </tr>
        
        {/* Expanded Child Rows - Individual Projects */}
        {isExpanded && group.projects.map((project, projectIndex) => {
          const projectBillableWithMargin = (project.billable_incurred || project.time_cost || 0) * (1 + activeMargin / 100);
          const projectFeesCollected = project.fees_collected || project.total_fees || 0;
          const projectOverservice = projectFeesCollected - projectBillableWithMargin;
          const projectCompletionPct = projectFeesCollected > 0 
            ? (projectBillableWithMargin / projectFeesCollected) * 100 
            : 0;
          
          const projectName = project.customer?.split(":")?.[1]?.trim() || project.customer;
          
          return (
            <tr key={`${uniqueKey}-child-${projectIndex}`} className="hover:bg-gray-50 bg-white">
              <td className="px-3 py-3 text-left pl-12">
                <div className="text-sm text-gray-700">
                  {projectName}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {formatProjectDate(project.project_start_date)} → {formatProjectDate(project.last_activity_date)}
                </div>
              </td>
              <td className="px-3 py-3 text-center text-sm text-gray-700">
                {formatCurrency(projectBillableWithMargin)}
              </td>
              <td className="px-3 py-3 text-center text-sm text-gray-700">
                {formatCurrency(projectFeesCollected)}
              </td>
              <td className={`px-3 py-3 text-center text-sm ${getOverserviceColor(projectOverservice)}`}>
                {formatCurrency(projectOverservice)}
              </td>
              <td className="px-3 py-3 text-center text-sm">
                <span className={`${projectCompletionPct > 100 ? 'text-red-600 font-medium' : 'text-gray-700'}`}>
                  {formatPercentage(projectCompletionPct / 100)}
                </span>
              </td>
            </tr>
          );
        })}
      </>
    );
  };

  const SectionHeader = ({ title, subtitle, definition }) => (
    <tr className="bg-gray-100">
      <td colSpan={5} className="px-3 py-2 text-left">
        <div className="flex items-center gap-2">
          <span className="font-bold text-gray-900">{title}</span>
          {definition && (
            <div className="group relative inline-flex">
              <InformationCircleIcon className="h-4 w-4 text-gray-500 hover:text-gray-700 cursor-help transition-colors" />
              <div className="absolute left-0 top-full mt-2 w-72 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50 shadow-xl">
                <div className="leading-relaxed">{definition}</div>
                <div className="absolute bottom-full left-4 -mb-1">
                  <div className="border-4 border-transparent border-b-gray-900"></div>
                </div>
              </div>
            </div>
          )}
        </div>
        {subtitle && <div className="text-xs text-gray-600 mt-0.5">{subtitle}</div>}
      </td>
    </tr>
  );

  return (
    <div className="space-y-4">
      {/* Main Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Margin Selector Header - Collapsible */}
        <div className="bg-brightblue-50 border-b border-blue-100 px-4 py-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              {periodLabel && <p className="text-sm font-medium text-blue-900">{periodLabel}</p>}
              
              {/* Toggle Button */}
              <button
                onClick={() => setShowMarginSelector(!showMarginSelector)}
                className="text-xs text-blue-700 hover:text-blue-900 underline flex items-center gap-1"
              >
                {showMarginSelector ? 'Hide' : 'Show'} Margin Settings
              </button>
            </div>
            
            {/* Margin Selector - Conditionally visible */}
            {showMarginSelector && (
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-blue-900">Margin:</label>
                <select
                  value={marginType}
                  onChange={(e) => setMarginType(e.target.value)}
                  className="border border-blue-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="0">At Cost (0%)</option>
                  <option value="25">25% Margin</option>
                  <option value="50">50% Margin</option>
                  <option value="75">75% Margin</option>
                  <option value="custom">Custom</option>
                </select>
                
                {/* Custom Margin Input */}
                {marginType === 'custom' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={customMargin}
                      onChange={(e) => setCustomMargin(e.target.value)}
                      min="0"
                      max="100"
                      step="0.5"
                      className="border border-blue-300 rounded-md px-3 py-1.5 text-sm w-20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="text-sm text-blue-900">%</span>
                  </div>
                )}
                
                {/* Margin Impact Indicator */}
                {activeMargin > 0 && (
                  <div className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded">
                    Showing {activeMargin}% markup applied
                  </div>
                )}
              </div>
            )}
            
            {/* Show active margin indicator even when collapsed */}
            {!showMarginSelector && activeMargin > 0 && (
              <div className="text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded">
                {activeMargin}% margin applied
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-brightblue-500">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-white uppercase">
                  Client/Project
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-white uppercase">
                  BILLABLE INCURRED
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-white uppercase">
                  INVOICED FEES
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-white uppercase">
                  OVER/UNDER SERVICE
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-white uppercase">
                  COMPLETION %
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Billed on 1st */}
              {billingBy1st.length > 0 && (
                <>
                  <SectionHeader 
                    title="Retainer - Billed on 1st" 
                    subtitle={cycle1stDates}
                    definition={KPI_DEFINITIONS.retainerCycle}
                  />
                  {billingBy1st.map((client, index) => (
                    <TableRow key={`1st-${index}`} client={client} index={index} section="1st" />
                  ))}
                </>
              )}

              {/* Billed on 15th */}
              {billingBy15th.length > 0 && (
                <>
                  <SectionHeader 
                    title="Retainer - Billed on 15th" 
                    subtitle={cycle15thDates}
                    definition={KPI_DEFINITIONS.retainerCycle}
                  />
                  {billingBy15th.map((client, index) => (
                    <TableRow key={`15th-${index}`} client={client} index={index} section="15th" />
                  ))}
                </>
              )}

              {/* Project Billing - Grouped */}
              {groupedProjects.length > 0 && (
                <>
                  <SectionHeader 
                    title="Project Billing" 
                    subtitle="Lifetime totals (click to expand individual projects)"
                    definition={KPI_DEFINITIONS.projectBilling}
                  />
                  {groupedProjects.map((group, index) => (
                    <ProjectGroupRow key={`project-group-${index}`} group={group} index={index} />
                  ))}
                </>
              )}

              {billingBy1st.length === 0 && billingBy15th.length === 0 && groupedProjects.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    No billing data available for this period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OverServicing;